import styled, { css } from 'styled-components';
import { useId, useState, type ReactNode } from 'react';
import { Flex } from 'antd';
import { CaretDownIcon } from '@phosphor-icons/react';
import { colors, motion, radii, spacing, typography } from '../../theme-v2';

/** The disclosure header's own padding — called out explicitly in the
 *  sidebar redline (asymmetric top/bottom) rather than sitting on the
 *  shared 4px spacing grid; the horizontal value still reuses spacing.base. */
const HEADER_PADDING = `20px ${spacing.base}px 14px`;

export interface CollapsibleSectionProps {
  /** Heading shown on the disclosure row. */
  title: ReactNode;
  /** Optional slot before the title — typically an icon. */
  icon?: ReactNode;
  /** Optional right-aligned slot (badge, button, status). */
  trailing?: ReactNode;
  /** Whether the panel starts open. Defaults to true. */
  defaultOpen?: boolean;
  /** When false, the section is permanently open and renders no toggle
   *  affordance — for cases where collapsing has nowhere to go. */
  collapsible?: boolean;
  /** Controlled open state. When provided, component becomes controlled. */
  open?: boolean;
  /** Callback fired when the user toggles. */
  onOpenChange?: (open: boolean) => void;
  /** Panel body. */
  children: ReactNode;
  /** Optional className for the wrapper. */
  className?: string;
}

/**
 * CollapsibleSection — disclosure pattern: a header with a caret icon
 * that toggles a body. Used for "▼ Knowledge Hub" / "▼ Chatbot" in
 * the sidebar, settings groups, etc.
 *
 * Uncontrolled by default (manages its own open state) but accepts
 * `open` / `onOpenChange` for controlled use when the parent needs to
 * sync collapse state with other UI.
 *
 * A11y: header is a real `<button>` with `aria-expanded` + `aria-controls`
 * pointing at the body. Body's `id` matches `aria-controls`. Screen
 * readers announce "Knowledge Hub, expanded button" / "collapsed".
 * With `collapsible={false}` the header is a plain element instead.
 */
export const CollapsibleSection = ({
  title,
  icon,
  trailing,
  defaultOpen = true,
  collapsible = true,
  open: openProp,
  onOpenChange,
  children,
  className,
}: CollapsibleSectionProps) => {
  const [internalOpen, setInternalOpen] = useState(defaultOpen);
  const isControlled = openProp !== undefined;
  const requestedOpen = isControlled ? openProp : internalOpen;
  // A non-collapsible section has no way to be closed, so `open` is ignored.
  const isOpen = collapsible ? requestedOpen : true;
  const bodyId = useId();

  const handleToggle = () => {
    const next = !isOpen;
    if (!isControlled) setInternalOpen(next);
    onOpenChange?.(next);
  };

  // A plain element, not a disabled button: nothing to focus, nothing
  // announced as a control.
  const headerProps = collapsible
    ? ({
        as: 'button',
        type: 'button',
        'aria-expanded': isOpen,
        'aria-controls': bodyId,
        onClick: handleToggle,
      } as const)
    : ({ as: 'div' } as const);

  return (
    <StyledSection className={className}>
      <StyledHeader {...headerProps} $interactive={collapsible}>
        {/* `component="span"` — the header is a <button>, whose content
            model only allows phrasing content, so this slot can't be a div. */}
        {icon && (
          <Flex component="span" align="center" flex="0 0 auto">
            {icon}
          </Flex>
        )}
        <StyledTitle>{title}</StyledTitle>
        {trailing && <StyledTrailing>{trailing}</StyledTrailing>}
        {collapsible && (
          <StyledCaret $open={isOpen}>
            <CaretDownIcon size={14} weight="bold" />
          </StyledCaret>
        )}
      </StyledHeader>
      <StyledBody id={bodyId} $open={isOpen} aria-hidden={!isOpen}>
        {children}
      </StyledBody>
    </StyledSection>
  );
};

const StyledSection = styled.section`
  display: flex;
  flex-direction: column;
  background: ${colors.surface.base};
  border-radius: ${radii.box}px;
  /* Grow to fill the parent when it's a flex container with bounded
     height (e.g. inside a SplitPanelItem); collapses to content-size
     in normal block flow. */
  flex: 1 1 auto;
  min-height: 0;
`;

const StyledHeader = styled.button<{ $interactive: boolean }>`
  display: flex;
  align-items: center;
  gap: ${spacing.sm}px;
  width: 100%;
  padding: ${HEADER_PADDING};
  background: transparent;
  border: none;
  border-bottom: 1px solid ${colors.border.muted};
  cursor: ${({ $interactive }) => ($interactive ? 'pointer' : 'default')};
  font-family: ${typography.fontFamily};
  color: ${colors.text.primary};
  text-align: left;
  transition: background ${motion.duration.fast} ${motion.easing.standard};

  &:focus-visible {
    outline: 2px solid ${colors.accent.base};
    outline-offset: -2px;
  }
`;

const StyledCaret = styled.span<{ $open: boolean }>`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  color: ${colors.text.secondary};
  transition: transform ${motion.duration.base} ${motion.easing.standard};
  flex-shrink: 0;

  ${({ $open }) =>
    !$open &&
    css`
      transform: rotate(-90deg);
    `}
`;

const StyledTitle = styled.span`
  flex: 1;
  font-size: ${typography.size.xl}px;
  font-weight: ${typography.weight.semibold};
  line-height: ${typography.lineHeight.tight};
  letter-spacing: ${typography.letterSpacing.tight};
`;

const StyledTrailing = styled.span`
  display: inline-flex;
  align-items: center;
  flex-shrink: 0;
  color: ${colors.text.secondary};
`;

/* Collapsed is hidden but still rendered: `display: none` would restart every
   CSS animation inside and zero the AntD tab metrics, so the quiz re-shakes and
   the ink bar re-draws on reopen. */
const StyledBody = styled(Flex).attrs({ vertical: true })<{
  $open: boolean;
}>`
  overflow: hidden;

  ${({ $open }) =>
    $open
      ? css`
          flex: 1 1 auto;
          min-height: 0;
        `
      : css`
          flex: 0 0 auto;
          height: 0;
          visibility: hidden;
        `}
`;
